import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import classes from "./record.module.scss";

/**
 * The record kit's one dropdown primitive — used by the header's "More" menu
 * and by StatusMenu. A plain popover (not react-bootstrap Dropdown) on the
 * --menu-* tokens; the wrapper is `position: relative`, the menu opens under
 * its trigger, right-aligned.
 */

/** Closes an open menu on an outside mouse-down or Escape. */
export const useMenuDismiss = (isOpen: boolean, wrapperRef: React.RefObject<HTMLElement>, close: () => void): void => {
    useEffect(() => {
        if (!isOpen) return;
        const handleOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) close();
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") close();
        };
        document.addEventListener("mousedown", handleOutside);
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleOutside);
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, wrapperRef, close]);
};

/** The positioned wrapper around a trigger and its menu. */
export const MenuAnchor = React.forwardRef<HTMLDivElement, { className?: string; children: React.ReactNode }>(
    ({ className, children }, ref) => (
        <div ref={ref} className={`${classes.more} ${className ?? ""}`}>{children}</div>
    ),
);
MenuAnchor.displayName = "MenuAnchor";

/** The popover list. */
export const Menu: React.FC<{ label: string; children: React.ReactNode; align?: "start" | "end" }> = ({ label, children, align = "end" }) => (
    <div className={`${classes.menu} ${align === "start" ? classes.menuStart : ""}`} role="menu" aria-label={label}>{children}</div>
);

interface MenuItemProps {
    /** Hover text. */
    title: string;
    icon?: string;
    danger?: boolean;
    disabled?: boolean;
    /** Why it is disabled, shown at the row end. */
    reason?: string;
    /** Marks the current choice (e.g. the current status). */
    isSelected?: boolean;
    onSelect?: () => void;
    to?: string;
    href?: string;
    className?: string;
    children: React.ReactNode;
}

/** One menu row: a button, an in-app link or a native link. */
export const MenuItem: React.FC<MenuItemProps> = ({
    title, icon, danger, disabled, reason, isSelected, onSelect, to, href, className, children,
}) => {
    const itemClass = ["btn-unstyled", classes.menuItem, danger ? classes.menuItemDanger : "", className ?? ""].join(" ");
    const content = (
        <>
            {icon && <i className={`bi bi-${icon}`} aria-hidden="true" />}
            <span>{children}</span>
            {isSelected && <i className="bi bi-check2" aria-hidden="true" />}
            {disabled && reason && <span className={classes.menuItemReason}>{reason}</span>}
        </>
    );
    if (!disabled && href) {
        return <a role="menuitem" className={itemClass} href={href} title={title} onClick={onSelect}>{content}</a>;
    }
    if (!disabled && to) {
        return <Link role="menuitem" className={itemClass} to={to} title={title} onClick={onSelect}>{content}</Link>;
    }
    return (
        <button
            type="button"
            role={isSelected !== undefined ? "menuitemradio" : "menuitem"}
            aria-checked={isSelected !== undefined ? isSelected : undefined}
            className={itemClass}
            title={title}
            disabled={disabled}
            onClick={onSelect}
        >
            {content}
        </button>
    );
};
